import { FieldEntity } from './field2.data-model';

/**
 * Row types a formula can attach to; decides its vocabulary and which grids
 * render it. A subset of the shared FieldEntity vocabulary custom fields use
 * (captableInvestment is the established value displayed as 'Security').
 */
export const FORMULA_FIELD_ENTITIES = [
  FieldEntity.company,
  FieldEntity.round,
  FieldEntity.captableInvestment,
] as const;

export type FormulaFieldEntity = (typeof FORMULA_FIELD_ENTITIES)[number];

/**
 * The records a formula reads through dot notation. Each entity exposes its
 * own row as one record (`FORMULA_ROW_RECORD`); a company formula also sees
 * the company record. Canonical refs are "<record>.<key>" paths, e.g.
 * "metrics.fmv" or "company.fund".
 */
export const FORMULA_RECORDS = ['metrics', 'company', 'round', 'security'] as const;

export type FormulaRecord = (typeof FORMULA_RECORDS)[number];

export const FORMULA_ROW_RECORD: Record<FormulaFieldEntity, FormulaRecord> = {
  company: 'metrics',
  round: 'round',
  captableInvestment: 'security',
};

export const FORMULA_COMPANY_RECORD = 'company' satisfies FormulaRecord;

/** What a formula can evaluate to, as the field stores it. Matches the engine's ValueType and the app's PrimitiveType. */
export const FORMULA_FIELD_TYPES = ['number', 'string', 'boolean', 'date'] as const;
export type FormulaFieldType = (typeof FORMULA_FIELD_TYPES)[number];

/** A stored type as the model accepts it; anything else (older data, an unknown value) reads as a number. */
export function normalizeFormulaFieldType(type: unknown): FormulaFieldType {
  return FORMULA_FIELD_TYPES.includes(type as FormulaFieldType) ? (type as FormulaFieldType) : 'number';
}

/**
 * A user-defined calculated metrics column (see specs/formula-fields.spec.md).
 * Mirrors the BE `FormulaField` entity, which extends the generic `FieldModel`
 * (same generic column names as company/person/client fields — no formMeta or
 * gridMeta). `formula` is canonical Excel-subset text with bracketed field
 * KEYS (record paths), e.g. "[metrics.fmv] / [metrics.amountInvested]" — display
 * paths only ever exist in the editor.
 */
export interface IFormulaFieldDataModel {
  id: number;
  key: string;
  displayName: string;
  description: string;
  /**
   * Which row type the formula evaluates against. Rows saved before entity
   * support (or an unrecognized BE value) normalize to 'company' at the
   * fetch boundary, so downstream code can rely on it.
   */
  entity: FormulaFieldEntity;
  /** A registered StandardFormatterId matching `type`, e.g. 'multiplier' or 'string'. */
  format: string | null;
  formula: string;
  /** The formula's result type — one of the engine's four value types. */
  type: FormulaFieldType;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
  updatedBy?: string | null;
}

/** Where a row carries its formula results: `row.formulaData.<key>`. */
export const FORMULA_DATA_PREFIX = 'formulaData';

export function formulaFieldPath(field: Pick<IFormulaFieldDataModel, 'key'>): string {
  return `${FORMULA_DATA_PREFIX}.${field.key}`;
}

/**
 * The fields attached to one entity — every column-injection and evaluation
 * site must filter through this so a formula never evaluates against (or
 * renders on) another entity's rows. Defensive fallback mirrors the fetch
 * normalization: a missing entity means a pre-entity company field.
 */
export function formulaFieldsForEntity(
  fields: IFormulaFieldDataModel[],
  entity: FormulaFieldEntity
): IFormulaFieldDataModel[] {
  return fields.filter((field) => (field.entity ?? 'company') === entity);
}
